import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { CheckCircle } from "lucide-react";
import { usePost } from "../../../../hooks/usePost";
import { useParams } from "react-router-dom";
import { useGetList } from "../../../../hooks/useGetList";

const PurchaseRequestApproveForm = ({ purchaseRequest }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm();
  const { id } = useParams();

  const { data, loading: accountLoading } = useGetList(
    `accounting-list/options`,
  );
  const { postData, loading } = usePost(`v2/purchase-request/${id}/approve`);

  const watchPaymentType = watch("paymentType");
  const paymentTypes = [
    {
      label: "Cash",
      value: "cash",
    },
    {
      label: "Bank",
      value: "bank",
    },
  ];

  const onFormSubmit = async (data) => {
    await postData(data);
  };
  const handleCancel = () => {};

  useEffect(() => {
    if (purchaseRequest) {
      setValue("paidAmount", purchaseRequest?.total);
    }
  }, [purchaseRequest]);

  return (
    <Card
      sx={{
        mt: 4,
        borderRadius: 3,
        boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
        border: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: "#e8f5e8",
              color: "#2e7d32",
            }}
          >
            <CheckCircle size={28} />
          </Box>
          <Typography variant="h5" fontWeight="bold" color="#2e7d32">
            Approve Purchase Request
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Purchase Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                {...register("purchaseDate", {
                  required: "Purchase date is required",
                })}
                error={!!errors.purchaseDate}
                helperText={errors.purchaseDate?.message}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="relatedAccount"
                control={control}
                rules={{ required: "Account is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel>Account</InputLabel>
                    <Select
                      {...field}
                      label="Account"
                      disabled={loading || accountLoading}
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      {!accountLoading &&
                        data?.data?.map((account) => (
                          <MenuItem key={account._id} value={account._id}>
                            {account.name}
                          </MenuItem>
                        ))}
                    </Select>
                    {error && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 2 }}
                      >
                        {error.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.paymentType}>
                <InputLabel>Payment Type</InputLabel>
                <Select
                  label="Payment Type"
                  {...register("paymentType", {
                    required: "Payment type is required",
                  })}
                  onChange={(e) => setValue("paymentType", e.target.value)}
                  value={watchPaymentType || ""}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  {paymentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.paymentType && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 2 }}
                  >
                    {errors.paymentType.message}
                  </Typography>
                )}
              </FormControl>
            </Grid> */}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Paid Amount"
                type="number"
                disabled
                InputProps={{
                  endAdornment: <Typography sx={{ ml: 1 }}>MMK</Typography>,
                }}
                {...register("paidAmount", {
                  required: "Paid amount is required",
                  min: {
                    value: 0,
                    message: "Amount must be greater than 0",
                  },
                })}
                error={!!errors.paidAmount}
                helperText={errors.paidAmount?.message}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                px: 4,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={<CheckCircle size={20} />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                px: 4,
                backgroundColor: "#2e7d32",
                "&:hover": {
                  backgroundColor: "#1b5e20",
                },
              }}
            >
              {loading ? "Approving..." : "Approve Request"}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default PurchaseRequestApproveForm;
